"use client"

import { useState, useTransition } from "react"
import { useRouter } from "next/navigation"
import { Trash2 } from "lucide-react"

interface DeleteButtonProps {
  resultId: string
}

// Rendered only after the server verifies that the signed-in user owns the
// row. The authenticated endpoint repeats the ownership check.
export function DeleteButton({ resultId }: DeleteButtonProps) {
  const router = useRouter()
  const [confirming, setConfirming] = useState(false)
  const [pending, startTransition] = useTransition()
  const [error, setError] = useState<string | null>(null)

  async function performDelete() {
    setError(null)
    try {
      const res = await fetch(`/api/user/analysis/${resultId}`, { method: "DELETE" })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setError(data.error || "Failed to delete")
        return
      }
      // Owner-scoped deletion succeeded. Refresh the route so it returns
      // notFound, then take the user back to the start.
      startTransition(() => {
        router.refresh()
        router.push("/")
      })
    } catch {
      setError("Network error. Try again.")
    }
  }

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-600 transition-colors"
      >
        <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
        Delete this result
      </button>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="text-gray-700">Delete this shared result?</span>
      <button
        type="button"
        onClick={performDelete}
        disabled={pending}
        className="inline-flex items-center gap-1 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold px-2.5 py-1 transition-colors disabled:opacity-60"
      >
        {pending ? "Deleting…" : "Yes, delete"}
      </button>
      <button
        type="button"
        onClick={() => setConfirming(false)}
        disabled={pending}
        className="inline-flex items-center gap-1 rounded-md border border-gray-300 hover:border-gray-400 text-gray-700 px-2.5 py-1 transition-colors"
      >
        Cancel
      </button>
      {error && <span className="text-red-600">{error}</span>}
    </div>
  )
}
